import { supabase } from "@/integrations/supabase/client";

export type ReadwiseIntegration = {
  id: string;
  status: string;
  last_synced_at: string | null;
  error_message: string | null;
  created_at: string;
};

export type ReadwiseIntegrationMeta = {
  tone: "default" | "destructive";
  badgeLabel: string;
  summary: string;
  detail: string;
  lastSyncLabel: string;
  needsReconnect: boolean;
  canSync: boolean;
};

export const formatReadwiseTimestamp = (value: string | null | undefined) => {
  if (!value) return "Never";
  return new Date(value).toLocaleString();
};

export const getReadwiseIntegrationMeta = (integration: ReadwiseIntegration | null): ReadwiseIntegrationMeta => {
  if (!integration) {
    return {
      tone: "default",
      badgeLabel: "Not connected",
      summary: "Readwise is not connected.",
      detail: "Connect your account to import highlights and reuse them in your source and excerpt workflows.",
      lastSyncLabel: "Never",
      needsReconnect: false,
      canSync: false,
    };
  }

  const status = integration.status.trim().toLowerCase();
  const lastSyncLabel = formatReadwiseTimestamp(integration.last_synced_at);
  const lastSyncMs = integration.last_synced_at ? new Date(integration.last_synced_at).getTime() : null;
  const isStale = Boolean(lastSyncMs && Date.now() - lastSyncMs > 1000 * 60 * 60 * 24 * 7);

  if (status === "connected") {
    return {
      tone: "default",
      badgeLabel: isStale ? "Connected · stale" : "Connected",
      summary: isStale ? "Readwise is connected, but the import looks stale." : "Readwise is connected and ready.",
      detail: isStale
        ? "Run a sync to refresh imported highlights and clear any backlog."
        : "Your highlights can be synced on demand and reused across your workspace.",
      lastSyncLabel,
      needsReconnect: false,
      canSync: true,
    };
  }

  if (status === "error" || integration.error_message) {
    return {
      tone: "destructive",
      badgeLabel: "Needs attention",
      summary: "Readwise needs attention before the next import.",
      detail: integration.error_message || "Reconnect your token or retry sync once the issue is resolved.",
      lastSyncLabel,
      needsReconnect: true,
      canSync: true,
    };
  }

  if (status === "disconnected") {
    return {
      tone: "destructive",
      badgeLabel: "Disconnected",
      summary: "Readwise was disconnected.",
      detail: "Reconnect your account to resume imports. Previously imported highlights stay available.",
      lastSyncLabel,
      needsReconnect: true,
      canSync: false,
    };
  }

  return {
    tone: "default",
    badgeLabel: integration.status,
    summary: "Readwise is connected with a non-standard status.",
    detail: integration.error_message || "You can retry a sync or reconnect if imports stop moving.",
    lastSyncLabel,
    needsReconnect: false,
    canSync: true,
  };
};

export async function fetchIntegration(profileId: string): Promise<ReadwiseIntegration | null> {
  const { data, error } = await supabase
    .from("user_integrations")
    .select("id, status, last_synced_at, error_message, created_at")
    .eq("profile_id", profileId)
    .eq("provider", "readwise")
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function testReadwiseConnection(token: string) {
  const { data, error } = await supabase.functions.invoke("readwise-test-connection", {
    body: { token },
  });
  if (error) throw error;
  return data as { ok: boolean };
}

export async function connectReadwise(profileId: string, token: string) {
  const { data, error } = await supabase.functions.invoke("readwise-connect", {
    body: { token, profile_id: profileId },
  });
  if (error) throw error;
  return data as { ok: boolean };
}

export async function disconnectReadwise(profileId: string) {
  const { data, error } = await supabase.functions.invoke("readwise-disconnect", {
    body: { profile_id: profileId },
  });
  if (error) throw error;
  return data as { ok: boolean };
}

export async function syncReadwise(workspaceId: string, profileId: string) {
  const { data, error } = await supabase.functions.invoke("readwise-sync", {
    body: { workspace_id: workspaceId, profile_id: profileId },
  });
  if (error) throw error;
  return data as { ok: boolean; imported: number };
}

export const READWISE_CATEGORIES = [
  { key: "books", label: "Books" },
  { key: "articles", label: "Articles" },
  { key: "podcasts", label: "Podcasts" },
  { key: "tweets", label: "Tweets" },
  { key: "supplementals", label: "Supplementals" },
] as const;

export type ReadwiseCategoryKey = (typeof READWISE_CATEGORIES)[number]["key"];


export async function fetchReadwiseCategories(profileId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("user_integrations")
    .select("synced_categories")
    .eq("profile_id", profileId)
    .eq("provider", "readwise")
    .maybeSingle();
  if (error) throw error;
  return (data?.synced_categories as string[] | null) ?? READWISE_CATEGORIES.map((c) => c.key);
}

export async function updateReadwiseCategories(profileId: string, categories: string[]) {
  const { error } = await supabase
    .from("user_integrations")
    .update({ synced_categories: categories })
    .eq("profile_id", profileId)
    .eq("provider", "readwise");
  if (error) throw error;
}

export async function purgeReadwise(workspaceId: string, mode: "supplementals" | "tweets" | "all") {
  const { data, error } = await supabase.functions.invoke("readwise-purge", {
    body: { workspace_id: workspaceId, mode },
  });
  if (error) throw error;
  return data as { ok: boolean; deleted: number };
}

export async function countReadwiseHighlights(workspaceId: string) {
  const { count, error } = await supabase
    .from("readwise_highlights")
    .select("id", { count: "exact", head: true })
    .eq("workspace_id", workspaceId);
  if (error) throw error;
  return count ?? 0;
}
